/**
 * Shared helpers for Indus Labs / Exotel click-to-call payloads.
 * Backend expects E.164-style numbers with 91 prefix (no leading 0).
 */

export function normalizeIndiaTel(raw) {
  if (raw == null || raw === '') return ''
  let digits = String(raw).replace(/\D/g, '')
  if (digits.startsWith('91') && digits.length >= 12) return digits.slice(0, 12)
  if (digits.startsWith('0')) digits = digits.slice(1)
  if (digits.length === 10) return `91${digits}`
  return digits
}

/** Strip country code; return 10-digit local number or empty string. */
export function toLocalIndianDigits(raw) {
  if (raw == null || raw === '' || raw === '—') return ''
  let digits = String(raw).replace(/\D/g, '')
  if (digits.length === 12 && digits.startsWith('91')) digits = digits.slice(2)
  else if (digits.length === 11 && digits.startsWith('0')) digits = digits.slice(1)
  return digits.length === 10 ? digits : ''
}

/** True when the number can be passed to Exotel click-to-call. */
export function isDialableIndianPhone(raw) {
  const digits = toLocalIndianDigits(raw)
  return digits.length === 10 && /^[6-9]\d{9}$/.test(digits)
}

function isInboundDirectionRaw(raw) {
  if (!raw) return false
  const lower = String(raw).toLowerCase()
  return lower.includes('inbound') || lower === 'incoming'
}

function parseCustomField(rawPayload = {}) {
  const makeCallReq = rawPayload.makeCallRequest || rawPayload.MakeCallRequest || {}
  const opts = makeCallReq.options || makeCallReq.Options || {}
  let customField = opts.customField ?? opts.CustomField ?? rawPayload.customField ?? rawPayload.CustomField
  if (typeof customField === 'string') {
    try { customField = JSON.parse(customField) } catch { customField = {} }
  }
  return customField || {}
}

function firstLocalIndianPhone(candidates) {
  for (const raw of candidates) {
    if (raw == null || raw === '' || raw === '—') continue
    const local = toLocalIndianDigits(raw)
    if (local) return local
  }
  return ''
}

/**
 * Resolve caller number for inbound call_logs rows.
 * Prefers webhook customer_number, then candidate contact fields, then From/To.
 */
export function resolveInboundCallerPhone(call = {}, rawPayload = {}, ctx = {}) {
  const webhook = rawPayload.webhook_response?.data || rawPayload.webhook_response || {}
  const makeCallReq = rawPayload.makeCallRequest || rawPayload.MakeCallRequest || {}
  const customField = parseCustomField(rawPayload)

  return firstLocalIndianPhone([
    webhook.customer_number,
    webhook.From,
    webhook.from,
    call.from_number,
    call.From,
    call.from,
    call.candidate_contact_no,
    call.contact_no,
    call.candidate_phone,
    call.mobile,
    call.phone,
    ctx.phone,
    ctx.contact_no,
    ctx.candidate_contact_no,
    customField.phone,
    customField.contact_no,
    makeCallReq.from,
    makeCallReq.From,
    rawPayload.from,
    rawPayload.From,
    call.to_number,
    call.To,
    call.to,
    webhook.To,
    webhook.to,
    rawPayload.to,
    rawPayload.To,
  ])
}

/**
 * Resolve callee number for outbound call_logs rows.
 * Prefers candidate-linked fields, then stored dial target / webhook To.
 */
export function resolveOutboundCalleePhone(call = {}, rawPayload = {}, ctx = {}) {
  const webhook = rawPayload.webhook_response?.data || rawPayload.webhook_response || {}
  const makeCallReq = rawPayload.makeCallRequest || rawPayload.MakeCallRequest || {}
  const opts = makeCallReq.options || makeCallReq.Options || {}
  const customField = parseCustomField(rawPayload)

  const identityFields = [
    call.candidate_contact_no,
    call.contact_no,
    call.candidate_phone,
    call.mobile,
    call.mobile_no,
    call.candidate_mobile,
    call.phone,
    ctx.phone,
    ctx.contact_no,
    ctx.candidate_contact_no,
    customField.phone,
    customField.contact_no,
    customField.candidate_phone,
    customField.to,
  ]

  const routingFields = [
    makeCallReq.to,
    makeCallReq.To,
    opts.to,
    rawPayload.to,
    rawPayload.To,
    call.to_number,
    call.To,
    call.to,
    webhook.To,
    webhook.to,
    webhook.DialWhomNumber,
    webhook.PhoneNumber,
    webhook.phone,
    webhook.customer_number,
  ]

  return firstLocalIndianPhone([...identityFields, ...routingFields])
}

/** Resolve dialable local phone for a call row (inbound or outbound). */
export function resolveCallRowPhone(call = {}, rawPayload = {}, ctx = {}, directionRaw = '') {
  return isInboundDirectionRaw(directionRaw)
    ? resolveInboundCallerPhone(call, rawPayload, ctx)
    : resolveOutboundCalleePhone(call, rawPayload, ctx)
}

function rowEmpId(row) {
  if (!row || typeof row !== 'object') return ''
  const raw = row.emp_id != null && String(row.emp_id).trim() !== ''
    ? row.emp_id
    : (row.empId != null && String(row.empId).trim() !== '' ? row.empId : row.title)
  return raw != null ? String(raw).trim() : ''
}

function rowCallerId(row) {
  if (!row || typeof row !== 'object') return ''
  const raw = row.callerId || row.caller_id || row.abbrev
  return raw != null ? String(raw).trim() : ''
}

/** Non-empty DID (callerId) for an emp_id from GET /api/admin/config/exotel. */
export function resolveCallerId(exotelConfig, empId) {
  if (!exotelConfig || !Array.isArray(exotelConfig) || empId == null || String(empId).trim() === '') return null
  const want = String(empId).trim()
  const row = exotelConfig.find(c => rowEmpId(c) === want)
  const did = rowCallerId(row)
  return did || null
}

/** Strip country/trunk prefixes so 0948…, 91948…, and 948… compare equal. */
export function normalizeDidDigits(raw) {
  let digits = String(raw == null ? '' : raw).replace(/\D/g, '')
  if (!digits) return ''
  if (digits.startsWith('91') && digits.length >= 12) digits = digits.slice(2)
  if (digits.startsWith('0') && digits.length === 11) digits = digits.slice(1)
  return digits
}

/** DID Assignment row for a From / callerId (emp_id + callerId). */
export function findDidAssignmentRow(exotelConfig, callerId) {
  if (!exotelConfig || !Array.isArray(exotelConfig) || callerId == null || callerId === '') return null
  const want = normalizeDidDigits(callerId)
  if (!want) return null
  return exotelConfig.find((row) => {
    const did = normalizeDidDigits(row && (row.callerId || row.caller_id))
    return did && did === want
  }) || null
}

/** Recruiter emp_id that owns the From DID (e.g. SH-265), or null if unmapped. */
export function resolveEmpIdByCallerId(exotelConfig, callerId) {
  const row = findDidAssignmentRow(exotelConfig, callerId)
  if (!row) return null
  const empId = row.emp_id != null ? row.emp_id : row.empId
  return empId != null && String(empId) !== '' ? String(empId) : null
}

export function buildExotelCallPayload({
  userContactNo,
  targetPhone,
  callerId,
  candidateId = null,
  jobId = null,
  recruiterEmpId = 'Unknown',
  candidateName = null,
}) {
  const from = normalizeIndiaTel(userContactNo)
  const to = normalizeIndiaTel(targetPhone)
  const did = normalizeIndiaTel(callerId) || String(callerId || '').replace(/\D/g, '')

  return {
    from,
    to,
    callerId: did,
    source: 'induslabs',
    options: {
      callType: 'trans',
      timeLimit: 2000,
      timeOut: 30,
      record: true,
      recordingChannels: 'dual',
      recordingFormat: 'mp3',
      customField: JSON.stringify({
        candidate_id: candidateId || null,
        candidate_name: candidateName || null,
        job_id: jobId || null,
        recruiter: recruiterEmpId || 'Unknown',
      }),
      statusCallback: 'https://ats.sattvahuman.in/api/exotel/webhook',
      statusCallbackEvents: ['terminal', 'answered'],
      statusCallbackContentType: 'application/json',
    },
  }
}

/** True when makeExotelCall returned a successful backend envelope. */
export function isExotelCallSuccess(res) {
  if (!res) return false
  if (typeof res.status === 'number' && res.status >= 400) return false
  return res.status === 'OK' || Boolean(res.data?.call_id || res.data?.data?.call_id)
}

/** Extract user-facing error from axios err.response or backend ERROR envelope. */
export function getExotelCallErrorMessage(res) {
  if (!res) return 'Call failed — no response from server'
  const code = res.data?.code || res.code
  if (code === 'CALLS_UNAVAILABLE') {
    return res.data?.message
      || 'Calling is paused. Outbound is blocked while unavailable.'
  }
  if (typeof res.status === 'number') {
    if (res.status === 404) {
      return 'Call API not found. Backend route POST /api/exotel/make-call may not be deployed.'
    }
    if (res.status === 403 || res.status === 409) {
      return res.data?.message
        || (res.status === 409
          ? 'Calling is paused. Outbound is blocked while unavailable.'
          : 'DID is not assigned to your account. Contact admin.')
    }
    return res.data?.message || `Call failed (HTTP ${res.status})`
  }
  return res.message || res.data?.message || 'Call failed'
}

const CONNECTED_TELEPHONY_STATUSES = new Set(['answered', 'completed'])

/** True when Exotel/telephony data shows the call was connected (not a pipeline/candidate status). */
export function isTelephonyConnectedCall(item) {
  if (!item) return false
  const rawStatus = String(item._rawStatus || item.status || '').toLowerCase()
  if (CONNECTED_TELEPHONY_STATUSES.has(rawStatus)) return true
  return (Number(item.durationSec) || 0) > 0
}
